import type { ShopifyApiVersion } from "../../connections/shopify/ShopifyApiVersion";
import {
  ShopifyCollectionKey,
  ShopifyPageKey,
  ShopifyProductImageKey,
  ShopifyProductKey,
  ShopifyProductVariantKey,
} from "../../connections/shopify/keys";
import type { ModelField } from "../../database/ModelField";
import type { ConstraintsIn, ValidatorOptionsIn } from "../../problems/Constraints";
import { FieldType } from "../../type-system/FieldType";
import { unthunk } from "../../utils";

export const ShopifyMetafieldModelRelations = {
  [ShopifyProductKey]: [ShopifyProductVariantKey, ShopifyProductImageKey],
};

export type ShopifyMetafieldFieldType =
  | FieldType.Number
  | FieldType.String
  | FieldType.Enum
  | FieldType.DateTime
  | FieldType.Email
  | FieldType.Color
  | FieldType.URL
  | FieldType.BelongsTo
  | FieldType.Boolean
  | FieldType.JSON;

export const GadgetFieldTypesThatTranslateToMetafields = [
  FieldType.Number,
  FieldType.String,
  FieldType.Enum,
  FieldType.DateTime,
  FieldType.Email,
  FieldType.Color,
  FieldType.URL,
  FieldType.BelongsTo,
  FieldType.Boolean,
  FieldType.JSON,
];

export const fieldTypeSupportsMetafields = (fieldType: FieldType) => {
  return GadgetFieldTypesThatTranslateToMetafields.includes(fieldType);
};

export const isMetafieldListType = (type: AnyShopifyMetafieldType) => {
  return PUBLIC_METAFIELD_LIST_TYPES.includes(type as ShopifyMetafieldListType);
};

export const SHOPIFY_METAFIELD_FIELD_FEATURE = "shopifyMetafields";

export const PUBLIC_METAFIELD_TYPES = [
  "single_line_text_field",
  "multi_line_text_field",
  "page_reference",
  "product_reference",
  "variant_reference",
  "file_reference",
  "number_integer",
  "number_decimal",
  "date",
  "date_time",
  "url",
  "json",
  "boolean",
  "color",
  "weight",
  "volume",
  "dimension",
  "rating",
  "collection_reference",
  "metaobject_reference",
  "mixed_reference",
  "money",
  "link",
] as const;

export const PUBLIC_METAFIELD_LIST_TYPES = [
  "list.single_line_text_field",
  "list.page_reference",
  "list.product_reference",
  "list.variant_reference",
  "list.file_reference",
  "list.number_integer",
  "list.number_decimal",
  "list.date",
  "list.date_time",
  "list.url",
  "list.color",
  "list.weight",
  "list.volume",
  "list.dimension",
  "list.rating",
  "list.collection_reference",
  "list.metaobject_reference",
  "list.mixed_reference",
  "list.link",
] as const;

export type ShopifyMetafieldListType = (typeof PUBLIC_METAFIELD_LIST_TYPES)[number];

export const PRIVATE_METAFIELD_TYPES = ["STRING", "JSON_STRING", "INTEGER"] as const;

export const publicMetaFieldsByVersion: {
  [key in ShopifyApiVersion]?: AnyShopifyMetafieldType[];
} = {
  "2022-01": [
    "single_line_text_field",
    "multi_line_text_field",
    "page_reference",
    "product_reference",
    "variant_reference",
    "file_reference",
    "number_integer",
    "number_decimal",
    "date",
    "date_time",
    "url",
    "json",
    "boolean",
    "color",
    "weight",
    "volume",
    "dimension",
    "rating",
    "money",
  ],
  "2022-10": [
    "collection_reference",
    "list.single_line_text_field",
    "list.page_reference",
    "list.product_reference",
    "list.variant_reference",
    "list.file_reference",
    "list.number_integer",
    "list.number_decimal",
    "list.date",
    "list.date_time",
    "list.url",
    "list.color",
    "list.weight",
    "list.volume",
    "list.dimension",
    "list.rating",
    "list.collection_reference",
  ],
  "2023-01": ["metaobject_reference", "mixed_reference", "list.metaobject_reference", "list.mixed_reference"],
  "2024-07": ["link"],
};

export type ShopifyMetafieldType = (typeof PUBLIC_METAFIELD_TYPES)[number];

export type ShopifyPrivateMetafieldType = (typeof PRIVATE_METAFIELD_TYPES)[number];

export type AnyShopifyMetafieldType = ShopifyMetafieldType | ShopifyPrivateMetafieldType | ShopifyMetafieldListType;

export const ShopifyMetafieldTypes: AnyShopifyMetafieldType[] = [
  ...PUBLIC_METAFIELD_TYPES,
  ...PRIVATE_METAFIELD_TYPES,
  ...PUBLIC_METAFIELD_LIST_TYPES,
];

export const getPrivateMetafieldTypeForField = (field: ModelField): ShopifyPrivateMetafieldType => {
  switch (field.fieldType) {
    case FieldType.Number:
      if (field.configuration.isFloat()) {
        return "STRING";
      } else {
        return "INTEGER";
      }
    case FieldType.JSON:
      return "JSON_STRING";
    default:
      return "STRING";
  }
};

export const getPublicMetafieldTypeForField = (field: ModelField): AnyShopifyMetafieldType => {
  switch (field.fieldType) {
    case FieldType.Number:
      if (field.configuration.isFloat()) {
        return "number_decimal";
      } else {
        return "number_integer";
      }
    case FieldType.URL:
      return "url";
    case FieldType.Color:
      return "color";
    case FieldType.BelongsTo:
      switch (field.configuration?.relatedModelKey) {
        case ShopifyProductKey:
          return "product_reference";
        case ShopifyProductVariantKey:
          return "variant_reference";
        case ShopifyPageKey:
          return "page_reference";
        case ShopifyCollectionKey:
          return "collection_reference";
        default:
          return "single_line_text_field";
      }
    case FieldType.DateTime:
      if (field.configuration.includeTime) return "date_time";
      else return "date";
    case FieldType.JSON:
      return "json";
    case FieldType.Boolean:
      return "boolean";
    default:
      return "single_line_text_field";
  }
};

const metafieldTypeStringTypes: (ShopifyMetafieldType | ShopifyPrivateMetafieldType)[] = [
  "single_line_text_field",
  "multi_line_text_field",
  "STRING",
];
const metafieldTypeNumberTypes: (ShopifyMetafieldType | ShopifyPrivateMetafieldType)[] = ["number_integer", "number_decimal", "INTEGER"];

export const possibleMetafieldTypesForFieldType = (fieldType: FieldType): AnyShopifyMetafieldType[] => {
  switch (fieldType) {
    case FieldType.String:
      return [
        ...metafieldTypeStringTypes,
        ...metafieldTypeNumberTypes,
        "color",
        "JSON_STRING",
        "product_reference",
        "page_reference",
        "variant_reference",
        "file_reference",
        "date",
        "date_time",
        "url",
        "collection_reference",
        "metaobject_reference",
        "mixed_reference",
      ];
    case FieldType.Enum:
      return [...metafieldTypeStringTypes, ...metafieldTypeNumberTypes];
    case FieldType.Email:
      return metafieldTypeStringTypes;
    case FieldType.Color:
      return [...metafieldTypeStringTypes, "color"];
    case FieldType.URL:
      return [...metafieldTypeStringTypes, "url"];
    case FieldType.DateTime:
      return [...metafieldTypeStringTypes, "date", "date_time"];
    case FieldType.Number:
      return [...metafieldTypeStringTypes, ...metafieldTypeNumberTypes];
    case FieldType.Boolean:
      return [...metafieldTypeStringTypes, "boolean"];
    case FieldType.JSON:
      return [
        ...metafieldTypeStringTypes,
        ...PUBLIC_METAFIELD_LIST_TYPES,
        "json",
        "JSON_STRING",
        "rating",
        "weight",
        "dimension",
        "volume",
        "money",
        "link",
      ];
    case FieldType.BelongsTo:
      return [
        ...metafieldTypeStringTypes,
        "number_integer",
        "INTEGER",
        "product_reference",
        "page_reference",
        "variant_reference",
        "collection_reference",
      ];
    default:
      return [];
  }
};

export const getMetafieldTypeForField = (field: ModelField): AnyShopifyMetafieldType => {
  if (field.contributorConfiguration[ShopifyMetafieldFieldContributorID]?.metafield?.isPrivate) {
    return getPrivateMetafieldTypeForField(field);
  } else {
    return getPublicMetafieldTypeForField(field);
  }
};

export type ShopifyMetafieldConfiguration = {
  namespace: string;
  key: string;
  isPrivate: boolean;
  type: AnyShopifyMetafieldType;
};

export interface IShopifyMetafieldFieldContributorConfiguration {
  isMetafield: boolean;
  metafield?: ShopifyMetafieldConfiguration;
}

export const ShopifyMetafieldFieldContributorID = "shopify/contributor/field/metafield";

export const translateMetafieldTypeFromPrivate = (type: ShopifyPrivateMetafieldType): ShopifyMetafieldType => {
  switch (type) {
    case "INTEGER":
      return "number_integer";
    case "JSON_STRING":
      return "json";
    default:
      return "single_line_text_field";
  }
};

export const translateMetafieldTypeToPrivate = (type: ShopifyMetafieldType): ShopifyPrivateMetafieldType => {
  switch (type) {
    case "number_integer":
      return "INTEGER";
    case "json":
      return "JSON_STRING";
    default:
      return "STRING";
  }
};

export const shopifyMetafieldContributorConfigValidators = (constraints: ConstraintsIn<Record<string, any>>) => {
  return Object.entries(constraints).reduce((map, [key, validatorOptions]) => {
    map[`contributorConfiguration.${ShopifyMetafieldFieldContributorID}.metafield.${key}`] = (
      value,
      node,
      attributeName,
      options,
      constraints
    ) => {
      if (!node.contributorConfiguration[ShopifyMetafieldFieldContributorID]?.isMetafield) return null;
      return unthunk(validatorOptions as ValidatorOptionsIn<ModelField>, value, node, attributeName, options, constraints);
    };
    return map;
  }, {} as ConstraintsIn<ModelField>);
};

export const SHOPIFY_METAFIELD_APP_OWNED_PREFIX_SHORT_PATTERN = "^\\$app:";
export const SHOPIFY_METAFIELD_APP_OWNED_PREFIX_PATTERN = "^app--(\\d+)--";
export const SHOPIFY_METAFIELD_NAMESPACE_AND_KEY_REGEXP = /(^\$app:)?[A-Za-z0-9_-]*/;
export const SHOPIFY_PRIVATE_METAFIELD_PER_RESOURCE_COUNT = 10;
export const SHOPIFY_METAFIELD_TYPE_LIST_PREFIX = "list.";

export const appOwnedMetafieldRegex = new RegExp(
  `(${SHOPIFY_METAFIELD_APP_OWNED_PREFIX_SHORT_PATTERN})|(${SHOPIFY_METAFIELD_APP_OWNED_PREFIX_PATTERN})`
);
export const stripAppOwnerFromNamespace = (namespace?: string) => namespace && namespace.replace(appOwnedMetafieldRegex, "");

export const getListMetafieldTypeForNonListType = (type: ShopifyMetafieldType) => {
  const newType = `${SHOPIFY_METAFIELD_TYPE_LIST_PREFIX}${type}`;
  if (!PUBLIC_METAFIELD_LIST_TYPES.includes(newType as ShopifyMetafieldListType)) return;

  return newType as ShopifyMetafieldListType;
};

export const getNonListMetafieldTypeForListType = (type: ShopifyMetafieldListType) => {
  const newType = type.slice(SHOPIFY_METAFIELD_TYPE_LIST_PREFIX.length);
  if (!ShopifyMetafieldTypes.includes(newType as ShopifyMetafieldType)) return;

  return newType as ShopifyMetafieldType;
};
